
import { Component, ViewChild, Output, EventEmitter, OnInit, Input, TemplateRef, OnDestroy } from '@angular/core';
import { SlickGridOptions, SlickGridColumnModel, SlickGridComponent, SlickContextMenuComponent, SlickGridColumnFilterTypes, SlickScreenBlockerService, SlickConfirmDialogComponent, SlickConfirmDialogResults, SlickToastService } from "@slick-components";
import { GlobalsService, AuthService, FunctionLockService, InvoicesService, UtilsService, PaymentProcessorService, HttpService } from '@services';
import { HttpHeaders } from '@angular/common/http';
import { DropdownModel, IDropdownModel, IInvoiceGridModel, IInvoiceModel, InvoiceModel } from '@models';
import { AccountingStore } from "@stores";
import * as moment from "moment";
import { EmailerDialogComponent } from "@components/emailer-dialog";
import { BatchPaymentDialogComponent } from "@shared-components/batch-payment-dialog"


@Component({
	selector: 'estimates-grid',
	templateUrl: './estimates-grid.component.html',
	styleUrls: ['./estimates-grid.component.scss'],
	host: { '[class.quote-infinite-grid]': 'infiniteScroll' },
	providers: [InvoicesService, SlickScreenBlockerService, SlickToastService, PaymentProcessorService]
})
export class EstimatesGridComponent implements OnInit, OnDestroy {
	readonly optimizeQuoteLoading = true;
	readonly batchSelectionColumns = [SlickGridColumnModel.addGridColumn({ columnKey: "BATCH_CHECKBOX", dataFieldName: "batch", width: "36px", sortable: false })];
	dashboardFilter: string = null;
	@Input() externalToolbar = false;
	@Input() infiniteScroll = false;
	@ViewChild('quoteToolbar', { static: true }) quoteToolbar: TemplateRef<any>;
	@Output() toolbarReady = new EventEmitter<TemplateRef<any>>();
	@Output() filtersChanged = new EventEmitter<any>();
	private destroyed = false;
	@Input() autoSelectFirstRow: boolean = false;
	@Input() customerId?: number;
	@Input() hideFilters: boolean = false;
	@Input() hideNewButton: boolean = false;
	@Output() onEstimateSelected = new EventEmitter<IInvoiceModel>();

	@ViewChild('estimatesGrid', { static: true }) estimatesGrid: SlickGridComponent;
	@ViewChild('estimatesGridContextMenuRef', { static: true }) estimatesGridContextMenuRef: SlickContextMenuComponent;
	@ViewChild("deleteEstimateConfirmRef", { static: true }) deleteEstimateConfirmRef: SlickConfirmDialogComponent;
	@ViewChild("emailerDialogRef") emailerDialogRef: EmailerDialogComponent;
	@ViewChild("batchPaymentDialogRef") batchPaymentDialogRef: BatchPaymentDialogComponent;



	gridOptions = new SlickGridOptions();
	selectedEstimate: IInvoiceModel;

	searchText: string;
	lastSearchString;
	searchTimeout: NodeJS.Timer;
	filteredLocationIds: number[] = [];
	isJellyfish: boolean = (GlobalsService.company.companyId === 133);
	showDealersOnly: boolean = (localStorage.getItem("INVOICE_SHOW_DEALERS_ONLY") || 'false').toLowerCase() === 'true';
	showInternalSales: boolean = (localStorage.getItem("INVOICE_SHOW_INTERNAL_SALES") || 'false').toLowerCase() === 'true';
	showPOSOnly: boolean = (localStorage.getItem("ESTIMATES_SHOW_POS_ONLY") || 'false').toLowerCase() === 'true';

	batchItems = [new DropdownModel(1, "Email"),
	new DropdownModel(2, "Print"),
	new DropdownModel(4, "Delete"),
	new DropdownModel(5, "To Sales Order"),
	new DropdownModel(6, "To Invoice")];

	nonConversionBatchItems: DropdownModel[] = [];

	estimateMonths = [
		new DropdownModel(1, "Past 1 Month"),
		new DropdownModel(3, "Past 3 Months"),
		new DropdownModel(6, "Past 6 Months"),
		new DropdownModel(12, "Past 12 Months"),
		new DropdownModel(24, "Past 24 Months"),
		new DropdownModel(0, "All Time")
	];

	batchActionId: number;
	isSubmitted: boolean;
	gridModelsForBatch: IInvoiceGridModel[] = [];

	selectedEstimateMonths = parseInt(localStorage.getItem("SELECTED_ESTIMATE_MONTHS") || '3');

	private contextMenuData: any;


	constructor(
		private authService: AuthService,
        private httpService: HttpService,
		private functionLockService: FunctionLockService,
		private slickScreenBlockerService: SlickScreenBlockerService,
		private slickToastService: SlickToastService,
		private invoicesService: InvoicesService,
		private accountingStore: AccountingStore) {
		this.gridOptions.gridKey = 'Estimates';
		this.gridOptions.striped = false;
		this.gridOptions.bordered = false;

		this.gridOptions.columns.push(SlickGridColumnModel.addGridColumn({ columnKey: "BATCH_CHECKBOX", title: '', dataFieldName: 'batch', default: true }))
		this.gridOptions.columns.push(SlickGridColumnModel.addGridColumn({ title: 'Quote #', dataFieldName: 'invoiceNumber', default: true/*, filterType: SlickGridColumnFilterTypes.text*/ }));
		this.gridOptions.columns.push(SlickGridColumnModel.addGridColumn({ title: 'PO #', dataFieldName: 'purchaseOrderNumber', default: true/*, filterType: SlickGridColumnFilterTypes.text*/ }));
		// If this is jellyfish, add this column as a default.  Everyone else gets it as optional
		const acceptedDateDefault = (GlobalsService.company.companyId === 133);
		this.gridOptions.columns.push(SlickGridColumnModel.addGridColumn({ columnKey: "ACCEPTED_DATE", title: 'Accepted Date', dataFieldName: 'acceptedDate', default: acceptedDateDefault/*, filterType: SlickGridColumnFilterTypes.text*/ }));
		this.gridOptions.columns.push(SlickGridColumnModel.addGridColumn({ title: 'Customer', dataFieldName: 'displayName', default: true/*, filterType: SlickGridColumnFilterTypes.text*/ }));
		this.gridOptions.columns.push(SlickGridColumnModel.addGridColumn({ title: 'First Name', dataFieldName: 'firstName', default: false/*, filterType: SlickGridColumnFilterTypes.text*/ }));
		this.gridOptions.columns.push(SlickGridColumnModel.addGridColumn({ title: 'Last Name', dataFieldName: 'lastName', default: false/*, filterType: SlickGridColumnFilterTypes.text*/ }));
		this.gridOptions.columns.push(SlickGridColumnModel.addGridColumn({ title: 'Full Name', dataFieldName: 'fullName', default: false/*, filterType: SlickGridColumnFilterTypes.text*/ }));
		this.gridOptions.columns.push(SlickGridColumnModel.addGridColumn({ title: 'Company', dataFieldName: 'companyName', default: false/*, filterType: SlickGridColumnFilterTypes.text*/ }));
		this.gridOptions.columns.push(SlickGridColumnModel.addGridColumn({ title: 'Address1', dataFieldName: 'address1', default: false/*, filterType: SlickGridColumnFilterTypes.text*/ }));
		this.gridOptions.columns.push(SlickGridColumnModel.addGridColumn({ title: 'Address2', dataFieldName: 'address2', default: false/*, filterType: SlickGridColumnFilterTypes.text*/ }));
		this.gridOptions.columns.push(SlickGridColumnModel.addGridColumn({ title: 'City', dataFieldName: 'city', default: false/*, filterType: SlickGridColumnFilterTypes.text*/ }));
		this.gridOptions.columns.push(SlickGridColumnModel.addGridColumn({ title: 'State', dataFieldName: 'state', default: false/*, filterType: SlickGridColumnFilterTypes.text*/ }));
		this.gridOptions.columns.push(SlickGridColumnModel.addGridColumn({ title: 'Zipcode', dataFieldName: 'zipcode', default: false/*, filterType: SlickGridColumnFilterTypes.text*/ }));
		this.gridOptions.columns.push(SlickGridColumnModel.addGridColumn({ title: 'Country', dataFieldName: 'country', default: false/*, filterType: SlickGridColumnFilterTypes.text*/ }));
		this.gridOptions.columns.push(SlickGridColumnModel.addGridColumn({ columnKey: "QUOTE_DATE", title: 'Quote Date', dataFieldName: 'quoteDate', default: true/*, filterType: SlickGridColumnFilterTypes.text*/ }));
		this.gridOptions.columns.push(SlickGridColumnModel.addGridColumn({ title: 'Total', dataFieldName: 'invoiceTotalDisplay', default: true/*, filterType: SlickGridColumnFilterTypes.text*/ }));
		this.gridOptions.columns.push(SlickGridColumnModel.addGridColumn({ title: 'Payments', dataFieldName: 'appliedPaymentsTotalDisplay', default: true/*, filterType: SlickGridColumnFilterTypes.text*/ }));
		this.gridOptions.columns.push(SlickGridColumnModel.addGridColumn({ title: 'Due', dataFieldName: 'outstandingBalanceDisplay', default: true/*, filterType: SlickGridColumnFilterTypes.text*/ }));
		this.gridOptions.columns.push(SlickGridColumnModel.addGridColumn({ columnKey: "SALES_REP", title: 'Sales Rep', dataFieldName: 'salesRepUserId', default: true/*, filterType: SlickGridColumnFilterTypes.text*/ }));

		this.gridOptions.getUrl = GlobalsService.apiUrl + '/invoices/getInvoicesForGridView';
		this.gridOptions.httpHeaders = new HttpHeaders()
			.append('Authorization', `Bearer ${this.authService.getToken()}`)
			.append("TimeZoneOffset", (moment().utcOffset() / 60).toString());
	}

	async ngOnInit() {
		if (GlobalsService.company.useCreditCardProcessor)
			this.batchItems.splice(2,0,new DropdownModel(3, "Pre-Pay"))
		this.nonConversionBatchItems = this.batchItems.filter(item => item.id !== 5 && item.id !== 6);
		this.searchText = this.accountingStore.searchText;
		if (this.externalToolbar) Promise.resolve().then(() => {
			if (!this.destroyed) this.toolbarReady.emit(this.quoteToolbar);
		});
		this.gridOptions.autoSelectFirstRow = this.autoSelectFirstRow;
		// The first time, let the onLocationsChanged refresh since we don't know what locations are selected
		//this.updateGrid();

	}

	ngOnDestroy(): void { this.destroyed = true; }

	async onBatchClicked(gridModel: IInvoiceGridModel, e: Event) {
		e.stopPropagation();

		this.gridModelsForBatch = this.gridModelsForBatch.filter(x => x.invoiceId !== gridModel.invoiceId);
		gridModel.batch = !gridModel.batch;
		if (gridModel.batch)
			this.gridModelsForBatch.push(gridModel);
    }

	async performBatch() {
		this.isSubmitted = true;
		if (!this.batchActionId || (this.dashboardFilter === 'won' && [5, 6].includes(this.batchActionId)))
            return;

        let batchEstimateIds = [];

		// no need to push the ids here for emailing because we will do that in the case 1 below
		if (this.batchActionId !== 1)
			this.gridModelsForBatch.forEach(x => {
				if (![5, 6].includes(this.batchActionId) || x.isQuote) batchEstimateIds.push(x.invoiceId);
			});

        switch (this.batchActionId) {
            case 1:
				let groupedInvoices = {};
				this.gridModelsForBatch.forEach(x => {
					if (!groupedInvoices[x.customerId]) {
						groupedInvoices[x.customerId] = [];
					}
					groupedInvoices[x.customerId].push(x.invoiceId);
				});

				for (const customerId of Object.keys(groupedInvoices)) {
					this.slickScreenBlockerService.block();

					try {
						let batchUuid = UtilsService.newGuid();

						const emailerModel = await this.invoicesService.prepareBatchInvoiceEmail(groupedInvoices[customerId], batchUuid);
						this.slickScreenBlockerService.unblock();

						let emailed = await this.emailerDialogRef.showDialog(emailerModel);
						if (emailed) {
							await Promise.all(groupedInvoices[customerId].map(x => this.invoicesService.addInvoiceEmailSentNote(x, true)));
							if (this.externalToolbar) this.filtersChanged.emit(this.getQuoteFilters());
						}
					} catch (error) {
						console.error("Error processing customer ID:", customerId, error);
						this.slickScreenBlockerService.unblock();
					}

				}
				break;
            case 2:
                let batchEstimateUuid = UtilsService.newGuid();

                const w = window.open('', '_blank');
                w.document.write('Generating pdf...');
                const batchInvoiceUrl = await this.invoicesService.printBatchInvoices(batchEstimateIds, batchEstimateUuid);
				w.location.href = batchInvoiceUrl;
				this.gridModelsForBatch.forEach(x => x.batch = false);
				this.gridModelsForBatch = [];
				break;
			case 3:
				let processedPayments = await this.batchPaymentDialogRef.showDialog(this.gridModelsForBatch);
                this.gridModelsForBatch.forEach(x => {
                    processedPayments.forEach(pp => {
                        if (pp.invoiceGridModels.includes(x)) {
                            x.batch = false;
                            this.gridModelsForBatch = this.gridModelsForBatch.filter(gm => gm !== x);
                        }
                    })
				})
				this.updateGrid();
				break;
			case 4:
				await Promise.all(batchEstimateIds.map(x => this.invoicesService.deleteInvoice(x)));
				this.updateGrid();
				this.slickToastService.showSuccess("Estimates deleted");
				this.gridModelsForBatch.forEach(x => x.batch = false);
				this.gridModelsForBatch = [];
				break;
			case 5:
				await this.invoicesService.convertBatchToSalesOrder(batchEstimateIds);
				this.updateGrid();
				this.gridModelsForBatch.forEach(x => x.batch = false);
				this.gridModelsForBatch = [];
				break;
			case 6:
				await this.invoicesService.convertBatchToInvoice(batchEstimateIds);
				this.updateGrid();
				this.gridModelsForBatch.forEach(x => x.batch = false);
				this.gridModelsForBatch = [];
				break;
		}
		
		
	}
	

	async onRowClicked(estimateGridModel: IInvoiceGridModel) {
		try {
			await this.functionLockService.lock("GET_ESTIMATE");
			// The full estimate is fetched before anything reacts to the click, so this is the only
			// feedback the user gets until the detail (or the customer page's dialog) appears
			this.slickScreenBlockerService.block();
			this.selectedEstimate = await this.invoicesService.getInvoice(estimateGridModel.invoiceId);
			this.onEstimateSelected.emit(this.selectedEstimate);
		}
		finally {
			this.slickScreenBlockerService.unblock();
			this.functionLockService.release("GET_ESTIMATE");
		}
	}
	async onRowContextMenu(contextMenuData) {
		(<MouseEvent>contextMenuData.mouseEvent).preventDefault();
		this.contextMenuData = contextMenuData;

		this.estimatesGridContextMenuRef.showContextMenu(contextMenuData.mouseEvent);
	}

	async contextMenuDeleteEstimate() {
		const estimateGridModel: IInvoiceGridModel = this.estimatesGrid.visibleData[this.contextMenuData.rowIdx];
		const confirmResult = await this.deleteEstimateConfirmRef.confirm();

		if (confirmResult === SlickConfirmDialogResults.Ok) {
			await this.invoicesService.deleteInvoice(estimateGridModel.invoiceId);
			if (this.externalToolbar) this.filtersChanged.emit(this.getQuoteFilters());

			this.estimatesGrid.visibleData = this.estimatesGrid.visibleData.filter(x => x.invoiceId !== estimateGridModel.invoiceId);
			if (this.selectedEstimate.invoiceId === estimateGridModel.invoiceId) {
				this.selectedEstimate = null;
				this.estimatesGrid.unselectRow();
				this.onEstimateSelected.emit(null);
			}

			this.slickToastService.showSuccess("Estimate Deleted", 1000);
		}

	}

	async onSearch(searchText) {
		this.searchText = searchText;

		this.updateGrid();
	}


	async onShowDealersOnlyChanged() {
		this.showDealersOnly = !this.showDealersOnly;

		localStorage.setItem("INVOICE_SHOW_DEALERS_ONLY", this.showDealersOnly.toString());
		this.updateGrid();
	}

	async onShowInternalSalesChanged() {
		this.showInternalSales = !this.showInternalSales;

		localStorage.setItem("INVOICE_SHOW_INTERNAL_SALES", this.showInternalSales.toString());
		this.updateGrid();
	}

	async onNewEstimate() {
		this.selectedEstimate = new InvoiceModel();
		this.selectedEstimate.isQuote = true;
		this.selectedEstimate.isSalesOrder = false;
		this.selectedEstimate.quoteDate = moment().startOf("day").toDate();
		this.selectedEstimate.invoiceNumber = await this.invoicesService.getNextInvoiceNumber();
		this.selectedEstimate.createdBy = GlobalsService.userInfo.userId;
		this.onEstimateSelected.emit(this.selectedEstimate);
	}

	async updateGrid() {
		if (this.infiniteScroll) {
			const params = this.getQuoteFilters();
			if (this.externalToolbar) this.filtersChanged.emit(params);
			await this.estimatesGrid.reloadGrid(params);
			return;
		}
		try {
			await this.functionLockService.lock("UPDATE_GRID");
			this.slickScreenBlockerService.block(500);

			const params = this.getQuoteFilters();
			if (this.externalToolbar) this.filtersChanged.emit(params);

			await this.estimatesGrid.reloadGrid(params);
			if (this.autoSelectFirstRow && this.estimatesGrid.getVisibleRecords().length > 0)
				this.estimatesGrid.selectRow(0);
		}
		finally {
			this.slickScreenBlockerService.unblock();
			await this.functionLockService.release("UPDATE_GRID");
		}
	}

	async onLocationsChanged(selectedLocationsIds: number[]) {
		this.filteredLocationIds = selectedLocationsIds;
		await this.updateGrid();
	}

	private getQuoteFilters(): any {
		return {
			...(this.externalToolbar ? { quoteDashboardFilter: this.dashboardFilter, selectedLocationsOnly: 'true', optimizeQuoteLoading: this.optimizeQuoteLoading ? 'true' : 'false' } : {}), searchString: this.searchText, estimateMonths: this.selectedEstimateMonths,
			estimatesOnly: true, filteredLocationIds: JSON.stringify(this.filteredLocationIds),
			showDealersOnly: this.showDealersOnly, showInternalSales: this.showInternalSales,
			showPOSOnly: this.showPOSOnly, customerId: this.customerId
		};
	}

	onInfiniteReset(): void {
		this.gridModelsForBatch = [];
	}

	clearFilters(locations: { selectAllLocations(): void }): void {
		this.searchText = '';
		this.selectedEstimateMonths = 3;
		this.showPOSOnly = false;
		this.showDealersOnly = false;
		this.showInternalSales = false;
		this.dashboardFilter = null;
		localStorage.setItem("SELECTED_ESTIMATE_MONTHS", '3');
		localStorage.setItem("ESTIMATES_SHOW_POS_ONLY", 'false');
		localStorage.setItem("INVOICE_SHOW_DEALERS_ONLY", 'false');
		localStorage.setItem("INVOICE_SHOW_INTERNAL_SALES", 'false');
		// The selector saves all allowed locations and emits one list/dashboard refresh.
		locations.selectAllLocations();
	}

	async onShowPOSOnlyChanged() {
		this.showPOSOnly = !this.showPOSOnly;
		localStorage.setItem("ESTIMATES_SHOW_POS_ONLY", this.showPOSOnly.toString().toLowerCase());
		await this.updateGrid();
  }
  
	onEstimateMonthsChanged(estimateMonth: IDropdownModel) {
		if (!estimateMonth || estimateMonth.id === this.selectedEstimateMonths)
			return;

		localStorage.setItem("SELECTED_ESTIMATE_MONTHS", estimateMonth.id.toString());
		this.selectedEstimateMonths = estimateMonth.id;

		this.updateGrid();
	}

	async exportToCSV(): Promise<void> {
		try {
			const blob = await this.httpService.getFileBlob('/reporting/exportEstimatesToCSV', {
				cid: GlobalsService.company.companyId, filteredLocationIds: JSON.stringify(this.filteredLocationIds),
				estimateMonths: this.selectedEstimateMonths, showPOSOnly: this.showPOSOnly,
				showDealersOnly: this.showDealersOnly, showInternalSales: this.showInternalSales,
				searchString: this.searchText, customerId: this.customerId, selectedLocationsOnly: this.externalToolbar, quoteDashboardFilter: this.externalToolbar ? this.dashboardFilter : null
			});
			const url = URL.createObjectURL(blob);
			const anchor = document.createElement('a');
			anchor.href = url;
			anchor.download = 'Quotes_Report.csv';
			document.body.appendChild(anchor);
			anchor.click();
			anchor.remove();
			setTimeout(() => URL.revokeObjectURL(url), 1000);
		} catch {
			this.slickToastService.showDanger('Unable to export quotes.');
		}
	}

	async refreshRow(estimateModel: IInvoiceModel) {
		if (this.dashboardFilter) { await this.updateGrid(); return; }
		if (this.externalToolbar) this.filtersChanged.emit(this.getQuoteFilters());
		// See if this got moved to invoice or sales order
		if (estimateModel.isQuote == false) {
			this.estimatesGrid.visibleData = this.estimatesGrid.visibleData.filter(x => x.invoiceId !== estimateModel.invoiceId);
			this.selectedEstimate = null;
			this.estimatesGrid.unselectRow();
			return;
		}

		const estimateGridModel = await this.invoicesService.getInvoiceForGridView(estimateModel.invoiceId);

		const existingEstimateIdx = this.estimatesGrid.visibleData.findIndex(x => x.invoiceId === estimateModel.invoiceId);
		if (existingEstimateIdx >= 0)
			this.estimatesGrid.updateRecord(existingEstimateIdx, estimateGridModel);
		else
			this.estimatesGrid.addNewRecord(estimateGridModel);

	}
}
